/**
 * El horario: turnos, descansos y capacidad en minutos (CB-24).
 *
 * Vive acá y no en el Worker ni en la pantalla porque los tres lo necesitan y
 * tiene que dar **el mismo número** en los tres. La previsualización que se ve
 * mientras se edita un turno y el valor que después devuelve el servidor salen
 * de esta misma función; si estuvieran duplicados, el día que uno se corrija
 * sin el otro la pantalla mostraría una capacidad que el sistema no cree.
 *
 * Todo son cadenas `HH:MM` en hora local de Costa Rica. Igual que en
 * `period.ts`, no se usa `Date`: acá no hay fechas, solo minutos dentro de un
 * día, y meter un `Date` en el medio solo agrega una zona horaria que no hace
 * falta.
 */

/** `HH:MM`, 24 h. */
export type HhMm = string;

const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function isHhMm(t: unknown): t is HhMm {
  return typeof t === 'string' && HHMM_RE.test(t);
}

/** Minutos transcurridos desde medianoche. `'05:30'` → 330. */
export function minutesOfDay(t: HhMm): number {
  const m = HHMM_RE.exec(t);
  if (!m) throw new RangeError(`hora inválida: ${t}`);
  return Number(m[1]) * 60 + Number(m[2]);
}

/** La vuelta: 330 → `'05:30'`. */
export function timeOfMinutes(n: number): HhMm {
  const clamped = Math.max(0, Math.min(24 * 60 - 1, Math.round(n)));
  return `${String(Math.floor(clamped / 60)).padStart(2, '0')}:${String(clamped % 60).padStart(2, '0')}`;
}

/* ── Los datos del negocio, confirmados por el dueño ───────────────────────── */

/**
 * La ventana de servicio: de 5 de la mañana a 5 de la tarde.
 *
 * No es una preferencia de la interfaz, es el horario en que Clean Blue limpia.
 * El calendario dibuja exactamente este rango, y **no se amplía** para que
 * entre una visita mal cargada: una visita fuera de la ventana es un dato que
 * hay que mirar, no una franja que falta.
 */
export const SERVICE_WINDOW_START: HhMm = '05:00';
export const SERVICE_WINDOW_END: HhMm = '17:00';

/** Lo que tarda una limpieza. Es el grano con el que se mide la capacidad. */
export const CLEANING_MINUTES = 15;

/**
 * Consultorio que nunca declaró su horario: se limpia **antes de las 10:00**
 * (P4.3, opción (a)).
 *
 * No se materializa como regla en `cleaning_availability_rules`: eso sería
 * inventar una ventana que el cliente no declaró y que después nadie sabría de
 * dónde salió. Es un default de lectura — quien programa lo aplica, y la
 * pantalla lo dice en voz alta.
 */
export const DEFAULT_CLEANING_DEADLINE: HhMm = '10:00';

/** ¿La hora cae dentro de la ventana de servicio? El fin es exclusivo. */
export function isWithinServiceWindow(t: HhMm): boolean {
  const m = minutesOfDay(t);
  return m >= minutesOfDay(SERVICE_WINDOW_START) && m < minutesOfDay(SERVICE_WINDOW_END);
}

/* ── El álgebra de intervalos ──────────────────────────────────────────────
 *
 * Todo lo que sigue —los descansos que restan, la ventana de limpieza que sale
 * de intersectar médicos, los ratos en que una trabajadora puede limpiar— es
 * el mismo problema: juntar, cortar y restar intervalos de un día. Se escribe
 * una vez acá, en minutos, y las tres cosas de arriba se apoyan en esto.
 *
 * `Span` es `[desde, hasta)` en minutos desde medianoche. **El final es
 * exclusivo**, igual que la ventana de servicio: por eso dos intervalos que se
 * tocan en un punto (7-8 y 8-9) no se solapan, que es exactamente la regla que
 * el dueño fijó para la intersección de los médicos. */

type Span = readonly [number, number];

/** Un rato del día, en el formato en que viaja por la API y por la pantalla. */
export interface TimeWindow {
  start_time: HhMm;
  end_time: HhMm;
}

function toSpans(windows: readonly TimeWindow[]): Span[] {
  return windows.map((w) => [minutesOfDay(w.start_time), minutesOfDay(w.end_time)] as Span);
}

function toWindows(spans: readonly Span[]): TimeWindow[] {
  return spans.map(([a, b]) => ({ start_time: timeOfMinutes(a), end_time: timeOfMinutes(b) }));
}

/** Los recorta a `[from, to)` y tira lo que queda sin duración. */
function clipSpans(spans: readonly Span[], from: number, to: number): Span[] {
  return spans
    .map(([a, b]) => [Math.max(a, from), Math.min(b, to)] as Span)
    .filter(([a, b]) => b > a);
}

/**
 * Ordena y fusiona lo que se solapa **y lo que se toca**.
 *
 * Que se fusione lo que se toca es deliberado y es lo contrario de lo que hace
 * la intersección: dentro de **una misma** declaración, 7-8 y 8-9 son un solo
 * rato de 7 a 9 —la persona sigue ahí a las 8—, mientras que entre
 * declaraciones **distintas** esos mismos dos intervalos no comparten ni un
 * minuto. Son dos preguntas diferentes sobre los mismos números.
 */
function mergeSpans(spans: readonly Span[]): Span[] {
  const sorted = spans.filter(([a, b]) => b > a).sort((x, y) => x[0] - y[0]);
  const out: Span[] = [];
  for (const [a, b] of sorted) {
    const last = out[out.length - 1];
    if (last && a <= last[1]) out[out.length - 1] = [last[0], Math.max(last[1], b)];
    else out.push([a, b]);
  }
  return out;
}

/** Lo que dos listas ya fusionadas tienen en común. Tocarse no cuenta. */
function intersectSpans(a: readonly Span[], b: readonly Span[]): Span[] {
  const out: Span[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    const from = Math.max(a[i]![0], b[j]![0]);
    const to = Math.min(a[i]![1], b[j]![1]);
    if (to > from) out.push([from, to]);
    // Avanza el que termina antes: el otro todavía puede cruzarse con el
    // siguiente de esta lista.
    if (a[i]![1] < b[j]![1]) i += 1;
    else j += 1;
  }
  return out;
}

/** Lo que queda de `whole` después de sacarle los huecos (ya fusionados). */
function subtractSpans(whole: Span, holes: readonly Span[]): Span[] {
  const out: Span[] = [];
  let cursor = whole[0];
  for (const [a, b] of holes) {
    if (b <= cursor || a >= whole[1]) continue;
    if (a > cursor) out.push([cursor, a]);
    cursor = Math.max(cursor, b);
  }
  if (cursor < whole[1]) out.push([cursor, whole[1]]);
  return out;
}

function totalMinutes(spans: readonly Span[]): number {
  return spans.reduce((sum, [a, b]) => sum + (b - a), 0);
}

/* ── Turnos ────────────────────────────────────────────────────────────────── */

export const BREAK_KIND = ['BREAKFAST', 'LUNCH', 'BREAK'] as const;
export type BreakKind = (typeof BREAK_KIND)[number];

export interface ShiftBreak {
  kind: BreakKind;
  start_time: HhMm;
  end_time: HhMm;
}

/** Un día de trabajo: de cuándo a cuándo, y qué ratos no se limpia. */
export interface WorkShift {
  /** 0 = domingo, como en `cleaning_availability_rules`. */
  weekday: number;
  start_time: HhMm;
  end_time: HhMm;
  breaks: ShiftBreak[];
}

export interface ShiftCapacity {
  /** Minutos entre la entrada y la salida. */
  span_minutes: number;
  /** Minutos de descanso que efectivamente caen dentro del turno. */
  break_minutes: number;
  /** Lo que queda para limpiar. */
  net_minutes: number;
  /** Cuántas limpiezas de 15 minutos entran ahí. */
  visits: number;
}

/**
 * Los descansos que de verdad restan.
 *
 * Se **recortan al turno** y se **fusionan entre sí** antes de sumarlos. No es
 * paranoia: el desayuno que el dueño describió es de 10:00 a 11:00 para las
 * dos trabajadoras, y una de ellas sale a las 10:00 — ese descanso queda
 * enteramente fuera de su turno y restarlo le quitaría una hora que sí
 * trabajó. Con dos descansos superpuestos pasa lo mismo al revés: sumarlos por
 * separado restaría dos veces los minutos compartidos y daría una capacidad
 * menor que la real.
 *
 * El formulario ya no deja cargar un descanso fuera del turno ni dos
 * encimados; esto es la red de abajo, para los datos que ya estén cargados y
 * para los que entren por otra vía.
 */
function effectiveBreakMinutes(shift: WorkShift): number {
  const from = minutesOfDay(shift.start_time);
  const to = minutesOfDay(shift.end_time);
  return totalMinutes(mergeSpans(clipSpans(toSpans(shift.breaks), from, to)));
}

/**
 * Los ratos en que esta persona **puede limpiar de verdad**: el turno, menos
 * los descansos, acotado a la ventana de servicio (P4.4).
 *
 * Acá es donde «los descansos son fijos» deja de ser una frase y se vuelve
 * ejecutable: el desayuno y el almuerzo no aparecen en esta lista, así que el
 * generador de visitas no tiene dónde meter una limpieza adentro de ellos. No
 * son huecos que se acomodan según la carga —son bloques que ya no existen
 * para el programador.
 *
 * Se recorta a 05:00-17:00 porque fuera de esa ventana no se limpia aunque la
 * persona esté trabajando: la ventana es del negocio, no del turno.
 */
export function cleanableWindows(shift: WorkShift): TimeWindow[] {
  const service = clipSpans(
    [[minutesOfDay(shift.start_time), minutesOfDay(shift.end_time)]],
    minutesOfDay(SERVICE_WINDOW_START), minutesOfDay(SERVICE_WINDOW_END));
  const whole = service[0];
  if (!whole) return [];
  return toWindows(subtractSpans(whole, mergeSpans(toSpans(shift.breaks))));
}

/** Cuántas limpiezas enteras entran en esos ratos, sin partir ninguna. */
export function visitsInWindows(windows: readonly TimeWindow[]): number {
  return toSpans(windows).reduce((n, [a, b]) => n + Math.floor((b - a) / CLEANING_MINUTES), 0);
}

/** La capacidad de un día. Se calcula: no hay una casilla donde teclearla. */
export function shiftCapacity(shift: WorkShift): ShiftCapacity {
  const span = minutesOfDay(shift.end_time) - minutesOfDay(shift.start_time);
  if (span <= 0) {
    return { span_minutes: 0, break_minutes: 0, net_minutes: 0, visits: 0 };
  }
  const breaks = effectiveBreakMinutes(shift);
  const net = Math.max(0, span - breaks);
  return {
    span_minutes: span,
    break_minutes: breaks,
    net_minutes: net,
    /* Las visitas NO son `net / 15`, y la diferencia importa: los minutos
       netos son tiempo trabajado —se pueden sumar—, pero una limpieza no se
       puede partir en dos alrededor del almuerzo ni empezar a las 16:50. Se
       cuentan por rato disponible, cada uno por su cuenta. Para los dos
       horarios reales el número coincide; para un turno picado, no. */
    visits: visitsInWindows(cleanableWindows(shift)),
  };
}

/** Minutos efectivos de un día. Atajo para cuando solo interesa el número. */
export function shiftMinutes(shift: WorkShift): number {
  return shiftCapacity(shift).net_minutes;
}

/** Minutos efectivos de toda la semana declarada. */
export function weeklyMinutes(shifts: readonly WorkShift[]): number {
  return shifts.reduce((total, s) => total + shiftMinutes(s), 0);
}

/** Los minutos efectivos de un día concreto, sumando a todo el personal. */
export function minutesOnWeekday(shifts: readonly WorkShift[], weekday: number): number {
  return weeklyMinutes(shifts.filter((s) => s.weekday === weekday));
}

/** Cuántas limpiezas de `CLEANING_MINUTES` entran en esos minutos. */
export function visitsThatFit(minutes: number): number {
  return Math.floor(Math.max(0, minutes) / CLEANING_MINUTES);
}

/**
 * Minutos en que **dos turnos coinciden**: la ventana pico.
 *
 * Con dos trabajadoras limpiando en paralelo, la capacidad de una franja no es
 * la suma de las jornadas sino cuánto se solapan — y es lo que decide si los
 * 24 consultorios entran antes del primer descanso.
 */
export function overlapMinutes(a: WorkShift, b: WorkShift): number {
  const from = Math.max(minutesOfDay(a.start_time), minutesOfDay(b.start_time));
  const to = Math.min(minutesOfDay(a.end_time), minutesOfDay(b.end_time));
  return Math.max(0, to - from);
}

/* ── La ventana de limpieza del consultorio (P4.2) ─────────────────────────
 *
 * 🔴 Es una INTERSECCIÓN, no una unión. Textual del dueño:
 *
 *   «Tiene que servirle a los tres, es como un conjunto de restricciones y
 *   intervalos posibles donde la intersección de los tres crea el verdadero
 *   window de limpieza.»
 *
 * Cada médico declara **cuándo se le puede limpiar a él**, y la ventana del
 * consultorio es lo que les sirve **a todos a la vez**. Si uno declara 7-9 y
 * otro 8-10, se limpia de 8 a 9: en cualquier otro momento habría alguien
 * atendiendo, y limpiar con el médico adentro es la falla que el servicio
 * promete no cometer.
 *
 * Leerlo como unión —«con que le sirva a uno alcanza»— daría 7-10 y mandaría a
 * la trabajadora a las 9:30, cuando el primero ya volvió. Por eso la operación
 * es la que es, aunque produzca ventanas más chicas.
 *
 * Cada declaración es una **lista** de intervalos porque un médico puede tener
 * más de un rato libre en el día; dentro de su lista los intervalos suman
 * (cualquiera de ellos le sirve), y entre médicos se cruzan.
 */

/** Lo que declaró una persona. Vacío significa que **no declaró**. */
export type DeclaredWindows = readonly TimeWindow[];

/**
 * De dónde salió la ventana con la que se va a programar.
 *
 * `conflict` existe para que el caso no se disuelva en silencio: cae al mismo
 * default que `undeclared`, pero por una razón muy distinta —acá sí declararon,
 * y lo que declararon no se puede cumplir junto—. Una ventana vacía es
 * información, no un no-evento.
 */
export type CleaningWindowSource = 'declared' | 'undeclared' | 'conflict';

export interface CleaningWindow {
  /** Los ratos en que se puede limpiar, ya acotados a 05:00-17:00. */
  windows: TimeWindow[];
  source: CleaningWindowSource;
  /** Cuántos declararon algo. Los que no declararon no cuentan ni restringen. */
  declarants: number;
  /** Cuánto mide la ventana en total. */
  minutes: number;
}

/**
 * El default de P4.3: si nadie declaró, se limpia **antes de las 10:00**.
 *
 * Es un rato, no una hora: el programador necesita saber desde cuándo puede
 * entrar, y eso es el arranque de la ventana de servicio.
 */
export const DEFAULT_CLEANING_WINDOW: TimeWindow = {
  start_time: SERVICE_WINDOW_START,
  end_time: DEFAULT_CLEANING_DEADLINE,
};

/**
 * La ventana real del consultorio: la intersección de lo que declararon sus
 * médicos, acotada a la ventana de servicio.
 *
 * Tres reglas que no se pueden leer del código de un vistazo:
 *
 *   1. **El que no declaró nada no restringe.** Su lista vacía se saltea. Lo
 *      contrario —tratarlo como «no disponible nunca»— dejaría la intersección
 *      vacía siempre que faltara un médico, que es casi siempre.
 *   2. **Tocarse no es solaparse.** 7-8 y 8-9 no comparten ningún minuto: la
 *      ventana queda vacía y el consultorio cae al default.
 *   3. **Intersección vacía → el default de P4.3**, decidido por el dueño: ni
 *      se avisa para renegociar, ni el consultorio sale de la programación.
 *      Pero vuelve marcado como `conflict`, que es lo que permite mostrarlo.
 *
 * Si alguien declaró **solo** ratos fuera de 05:00-17:00, su declaración queda
 * vacía al recortarla y el resultado es un conflicto, no un «no declaró». Es
 * correcto: esa persona sí puso una condición, y es una condición imposible.
 */
export function resolveCleaningWindow(
  declarations: readonly DeclaredWindows[],
): CleaningWindow {
  const from = minutesOfDay(SERVICE_WINDOW_START);
  const to = minutesOfDay(SERVICE_WINDOW_END);

  let current: Span[] = [[from, to]];
  let declarants = 0;

  for (const declared of declarations) {
    if (declared.length === 0) continue;
    declarants += 1;
    current = intersectSpans(current, mergeSpans(clipSpans(toSpans(declared), from, to)));
  }

  if (declarants === 0 || current.length === 0) {
    return {
      windows: [DEFAULT_CLEANING_WINDOW],
      source: declarants === 0 ? 'undeclared' : 'conflict',
      declarants,
      minutes: minutesOfDay(DEFAULT_CLEANING_WINDOW.end_time)
        - minutesOfDay(DEFAULT_CLEANING_WINDOW.start_time),
    };
  }

  return {
    windows: toWindows(current),
    source: 'declared',
    declarants,
    minutes: totalMinutes(current),
  };
}

/** «4 h 30 min», «45 min». Para mostrar minutos sin que nadie divida a mano. */
export function durationLabel(minutes: number): string {
  const safe = Math.max(0, Math.round(minutes));
  const h = Math.floor(safe / 60);
  const m = safe % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

/** «05:00-10:00 · 11:00-12:30». Una ventana partida se lee como lo que es. */
export function windowsLabel(windows: readonly TimeWindow[]): string {
  return windows.map((w) => `${w.start_time}-${w.end_time}`).join(' · ');
}

/* ── Lo que la base no puede comprobar sola ────────────────────────────────── */

/**
 * Un problema del horario, con el día al que pertenece.
 *
 * Vive acá y no en el Worker por la misma razón que la capacidad: la pantalla
 * tiene que poder avisar **mientras se edita**, y el servidor tiene que
 * rechazar lo mismo al guardar. Con la regla escrita dos veces, un día una
 * pantalla deja pasar algo que el servidor rechaza y la persona no entiende por
 * qué no puede guardar.
 *
 * El `CHECK` de la tabla garantiza que un descanso termine después de empezar;
 * lo de acá es lo que un `CHECK` de fila no puede mirar: la fila padre y las
 * hermanas.
 */
export interface ShiftProblem {
  weekday: number;
  code: 'invalid_shift' | 'overlapping_shifts' | 'invalid_break'
  | 'break_outside_shift' | 'overlapping_breaks';
  message: string;
}

export function findShiftProblems(shifts: readonly WorkShift[]): ShiftProblem[] {
  const problems: ShiftProblem[] = [];
  const byWeekday = new Map<number, [number, number][]>();

  for (const shift of shifts) {
    if (!isHhMm(shift.start_time) || !isHhMm(shift.end_time)) {
      problems.push({
        weekday: shift.weekday, code: 'invalid_shift',
        message: 'Falta la hora de entrada o la de salida',
      });
      continue;
    }
    const from = minutesOfDay(shift.start_time);
    const to = minutesOfDay(shift.end_time);
    if (to <= from) {
      problems.push({
        weekday: shift.weekday, code: 'invalid_shift',
        message: 'La salida tiene que ser posterior a la entrada',
      });
      continue;
    }

    // Dos turnos del mismo día que se pisan contarían dos veces los mismos
    // minutos y dirían que la persona puede estar en dos lados a la vez.
    const others = byWeekday.get(shift.weekday) ?? [];
    if (others.some(([a, b]) => from < b && to > a)) {
      problems.push({
        weekday: shift.weekday, code: 'overlapping_shifts',
        message: 'Hay dos turnos de ese día que se superponen',
      });
    }
    others.push([from, to]);
    byWeekday.set(shift.weekday, others);

    const taken: [number, number][] = [];
    for (const brk of shift.breaks) {
      if (!isHhMm(brk.start_time) || !isHhMm(brk.end_time)) {
        problems.push({
          weekday: shift.weekday, code: 'invalid_break',
          message: 'Falta la hora de un descanso',
        });
        continue;
      }
      const bFrom = minutesOfDay(brk.start_time);
      const bTo = minutesOfDay(brk.end_time);
      if (bTo <= bFrom) {
        problems.push({
          weekday: shift.weekday, code: 'invalid_break',
          message: 'El descanso tiene que terminar después de empezar',
        });
        continue;
      }
      if (bFrom < from || bTo > to) {
        problems.push({
          weekday: shift.weekday, code: 'break_outside_shift',
          message: 'El descanso cae fuera del turno de ese día',
        });
        continue;
      }
      if (taken.some(([a, b]) => bFrom < b && bTo > a)) {
        problems.push({
          weekday: shift.weekday, code: 'overlapping_breaks',
          message: 'Hay dos descansos de ese día que se superponen',
        });
        continue;
      }
      taken.push([bFrom, bTo]);
    }
  }

  return problems;
}

/** Días de la semana, con 0 = domingo. El mismo orden que guarda la base. */
export const WEEKDAY_NAME = [
  'Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado',
] as const;

export const WEEKDAY_ABBR = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'] as const;

/** Lunes a viernes: los días que Clean Blue opera. */
export const BUSINESS_WEEKDAYS = [1, 2, 3, 4, 5] as const;
